/** Partner page */
import { Grid, makeStyles, Typography } from '@material-ui/core'
import { GetStaticProps } from 'next'
import React from 'react'

import Link from '@aletheia/common/components/Link'
import PageComponent from '@aletheia/common/components/Page'
import TeamMemberCard from '../components/Team/TeamMemberCard'
import { getEntry } from '@aletheia/common/lib/contentful'
import { formatTextList } from '@aletheia/common/utils/formatters'

import Markdown from '../components/Markdown'
import {
  IPerson,
  fetchPage,
  IPage,
  IFund,
  isEntry,
  fetchSeriesEntries,
  IStaffMember,
  IOrganization,
} from '../contentful'

const useStyles = makeStyles((theme) => ({
  allMemberContainer: {
    marginBottom: theme.spacing(4),
  },
  teamMemberContainer: {
    marginBottom: theme.spacing(1),
  },
  teamMemberWrapper: {
    display: 'flex',
    alignItems: 'stretch',
  },
  bodyMarkdown: {
    '& *': {
      fontFamily: theme.typography.h1.fontFamily,
    },
  },
}))

type TeamPageProps = {
  Team: IPerson[]
  Funds: IFund[]
  Page: IPage
  Advisors: Advisor[]
}

type Advisor = {
  Person: IPerson
  Funds: IFund[]
}

const getStaffMembers = (
  Fund: IFund['fields'],
  filter: (p: IStaffMember) => boolean,
): IStaffMember[] => {
  return Fund.staffMembers?.filter(isEntry).filter(filter) || []
}

const TeamPage: React.FC<TeamPageProps> = ({ Team, Page, Funds, Advisors }) => {
  const classes = useStyles()
  const { title, body, bannerImage, shortTitle } = Page.fields

  const advisorFundsToSentence = (Advisor: Advisor): string => {
    return formatTextList(Advisor.Funds.map((Fund) => Fund.fields.title))
  }

  return (
    <PageComponent title={title} pageTitle={shortTitle} banner={bannerImage}>
      <Markdown className={classes.bodyMarkdown}>{body}</Markdown>
      <Typography variant="h4" component="h2" gutterBottom>
        EA Funds Team
      </Typography>
      <div className={classes.allMemberContainer}>
        <Grid container spacing={2} className={classes.teamMemberContainer}>
          {Team.map((TeamMember) => (
            <Grid
              item
              xs={12}
              md={6}
              key={TeamMember.sys.id}
              className={classes.teamMemberWrapper}
            >
              <TeamMemberCard
                Person={TeamMember}
                jobTitle={TeamMember.fields.jobTitle || ''}
                longJobTitle={
                  <>
                    {TeamMember.fields.jobTitle}{' '}
                    {isEntry(TeamMember.fields.organization) && (
                      <>at {TeamMember.fields.organization.fields.title}</>
                    )}
                  </>
                }
              />
            </Grid>
          ))}
        </Grid>
      </div>

      <Typography variant="h4" component="h2" gutterBottom>
        Our Fund managers
      </Typography>
      {Funds.map((Fund) => {
        return (
          <div key={Fund.sys.id}>
            <Typography variant="h5" component="h3" gutterBottom>
              <Link href={`/funds/${Fund.fields.slug}`}>
                {Fund.fields.title} Fund
              </Link>
            </Typography>

            <StaffBoxes Fund={Fund.fields} />
          </div>
        )
      })}

      <Typography variant="h5" component="h2" gutterBottom>
        Fund advisors
      </Typography>
      <div className={classes.allMemberContainer}>
        <Grid container spacing={2} className={classes.teamMemberContainer}>
          {Advisors.map((Advisor) => (
            <Grid
              item
              xs={12}
              md={6}
              key={Advisor.Person.sys.id}
              className={classes.teamMemberWrapper}
            >
              <TeamMemberCard
                Person={Advisor.Person}
                Organization={getEntry(Advisor.Person.fields.organization)}
                jobTitle={advisorFundsToSentence(Advisor)}
                longJobTitle={`Fund Advisor at ${advisorFundsToSentence(
                  Advisor,
                )}`}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    </PageComponent>
  )
}

export const StaffBoxes = ({ Fund }: { Fund: IFund['fields'] }) => {
  const classes = useStyles()
  const mainStaff = getStaffMembers(
    Fund,
    (staff) =>
      !['Guest Fund Manager', 'Assistant Fund Manager'].includes(
        staff.fields.jobTitle,
      ),
  )
  const guestAndAssistant = getStaffMembers(Fund, (staff) =>
    ['Guest Fund Manager', 'Assistant Fund Manager'].includes(
      staff.fields.jobTitle,
    ),
  )

  return (
    <div className={classes.allMemberContainer}>
      <TeamCardGrid Fund={Fund} staff={mainStaff} />

      {guestAndAssistant.length > 0 && (
        <TeamCardGrid Fund={Fund} staff={guestAndAssistant} />
      )}
    </div>
  )
}

const TeamCardGrid = ({
  Fund,
  staff,
  compact = false,
}: {
  Fund: IFund['fields']
  staff: IStaffMember[]
  compact?: boolean
}) => {
  const classes = useStyles()
  return (
    <Grid container spacing={2} className={classes.teamMemberContainer}>
      {staff.map((StaffMember) => (
        <Grid
          item
          xs={12}
          md={compact ? 4 : 6}
          key={StaffMember.sys.id}
          className={classes.teamMemberWrapper}
        >
          <TeamMemberCard
            compact={compact}
            Person={StaffMember.fields.person as IPerson}
            jobTitle={StaffMember.fields.jobTitle}
            longJobTitle={`${Fund.title} ${StaffMember.fields.jobTitle}`}
            Organization={
              (StaffMember.fields.person as IPerson).fields
                .organization as IOrganization
            }
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default TeamPage

export const getStaticProps: GetStaticProps<TeamPageProps> = async (_ctx) => {
  const [Page, Team, Funds] = await Promise.all([
    fetchPage('team'),
    fetchSeriesEntries('ea-funds-team'),
    fetchSeriesEntries('ordered-funds'),
  ])

  if (!Page) throw new Error('Missing Page')
  if (!Team) throw new Error('Missing EA Funds Team')
  if (!Funds) throw new Error('Missing Funds')

  // Transforms Funds.Advisors into an array that is inversed => Advisor.Funds
  const Advisors: Advisor[] = []
  Funds.forEach((Fund) => {
    if (Fund.fields.advisors) {
      Fund.fields.advisors.forEach((Person: IPerson) => {
        // Check if user is already in the Advisors array
        const index = Advisors.findIndex((a) => a.Person === Person)
        if (index === -1) {
          // Person is not in the Advisors array -> Add Person and initial Fund to the list
          Advisors.push({
            Person: Person,
            Funds: [Fund],
          })
        } else {
          // Person is already in the Advisors array -> Append only the Fund
          Advisors[index].Funds.push(Fund)
        }
      })
    }
  })

  return {
    props: { Team, Page, Funds, Advisors },
    revalidate: 1 * 60,
  }
}
